/**
 * User-facing copy for the account lifecycle (soft-delete / restore) flows:
 * why a target was skipped, and what a completed action did. Shared by the
 * settings Danger Zone and the user grid's selection actions so both
 * surfaces explain the same buckets the same way.
 *
 * Contains:
 * - lifecycleBlockerMessage — copy for one preflight/result blocker code
 * - lifecycleSuccessMessage — toast copy for an applied action
 *
 * Returns plain strings — no JSX.
 */

import { LIFECYCLE_ACTIONS } from './userLifecycle';

/**
 * Copy for one blocker code from `summarizePreflight` / `summarizeOutcome`.
 * Single-target wording when `isPlural` is false; counted wording otherwise.
 *
 * @param {import('i18next').TFunction} t
 * @param {string | undefined} code
 * @param {{ count?: number, isPlural?: boolean }} [opts]
 * @returns {string}
 */
export function lifecycleBlockerMessage(t, code, { count = 1, isPlural = false } = {}) {
  if (!isPlural) return singleBlockerMessage(t, code);
  switch (code) {
    case 'self_excluded':
      return t('userLifecycle.blockers.selfExcludedMany', 'Your own account is in the selection and will be skipped.');
    case 'superuser_excluded':
      return t('userLifecycle.blockers.superuserExcludedMany', {
        count,
        defaultValue_one: '{{count}} superuser account will be skipped; superusers cannot be deleted.',
        defaultValue_other: '{{count}} superuser accounts will be skipped; superusers cannot be deleted.',
      });
    case 'already_deleted':
      return t('userLifecycle.blockers.alreadyDeletedMany', {
        count,
        defaultValue_one: '{{count}} account is already deleted and will be skipped.',
        defaultValue_other: '{{count}} accounts are already deleted and will be skipped.',
      });
    case 'already_active':
      return t('userLifecycle.blockers.alreadyActiveMany', {
        count,
        defaultValue_one: '{{count}} account is already active and will be skipped.',
        defaultValue_other: '{{count}} accounts are already active and will be skipped.',
      });
    case 'conflicts':
      return t('userLifecycle.blockers.emailConflictMany', {
        count,
        defaultValue_one:
          '{{count}} account cannot be restored because its original email address now belongs to another account.',
        defaultValue_other:
          '{{count}} accounts cannot be restored because their original email addresses now belong to other accounts.',
      });
    case 'not_found':
      return t('userLifecycle.blockers.notFoundMany', {
        count,
        defaultValue_one: '{{count}} account is outside your customer or no longer exists and will be skipped.',
        defaultValue_other: '{{count}} accounts are outside your customer or no longer exist and will be skipped.',
      });
    default:
      return t('userLifecycle.blockers.failedMany', {
        count,
        defaultValue_one: 'The change could not be applied to {{count}} account.',
        defaultValue_other: 'The change could not be applied to {{count}} accounts.',
      });
  }
}

function singleBlockerMessage(t, code) {
  switch (code) {
    case 'self_excluded':
      return t('userLifecycle.blockers.selfExcluded', 'You cannot delete your own account.');
    case 'superuser_excluded':
      return t('userLifecycle.blockers.superuserExcluded', 'Superuser accounts cannot be deleted.');
    case 'already_deleted':
      return t('userLifecycle.blockers.alreadyDeleted', 'This account has already been deleted.');
    case 'already_active':
      return t('userLifecycle.blockers.alreadyActive', 'This account is already active.');
    case 'conflicts':
      return t(
        'userLifecycle.blockers.emailConflict',
        "This account's original email address now belongs to another account, so it cannot be restored.",
      );
    case 'not_found':
      return t('userLifecycle.blockers.notFound', 'This account is outside your customer or no longer exists.');
    default:
      return t('userLifecycle.blockers.failed', 'The change could not be applied to this account.');
  }
}

/**
 * Toast copy for an applied action.
 *
 * @param {import('i18next').TFunction} t
 * @param {'delete'|'restore'} action
 * @param {number} count — accounts the action actually changed
 * @returns {string}
 */
export function lifecycleSuccessMessage(t, action, count) {
  if (action === LIFECYCLE_ACTIONS.DELETE) {
    return t('userLifecycle.deleted', {
      count,
      defaultValue_one: 'User deleted.',
      defaultValue_other: '{{count}} users deleted.',
    });
  }
  return t('userLifecycle.restored', {
    count,
    defaultValue_one: 'User restored.',
    defaultValue_other: '{{count}} users restored.',
  });
}
